import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  signal,
  ViewChild
} from '@angular/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { NgClass } from '@angular/common';
import { CopyToClipboardService } from '@services/copy-to-clipboard.service';
import { ToastService } from '@services/toast/toast.service';
import { AnalyticsMetricsService } from '@services/analytics-metrics.service';
import { concatMap, filter, takeUntil } from 'rxjs/operators';
import { ChartData } from '@app/analytics/metric-widget/data-objects/chart-data';
import { QueryResult } from '@app/analytics/metric-widget/data-objects/query-result';
import { RenderableChart } from '@app/analytics/metric-widget/data-objects/renderable-chart';
import { DataPoint } from '@app/analytics/metric-widget/data-objects/data-point';
import { IQueryContext } from '@app/analytics/metric-widget/query/context';
import { Pair } from '@app/analytics/metric-widget/data-objects/pair';
import { Metric } from '@app/analytics/metric-widget/data-objects/metric-types';
import { DataType } from '@app/shared/models/sensor-node-data-type';
import { TimeNavigatorService } from '@app/shared/time/time-navigator.service';
import { Building } from '@app/shared/models/building.interface';
import { QueryExecutorService } from '@app/analytics/metric-widget/query/query-executor.service';

@Component({
  selector: 'app-metric-widget',
  templateUrl: './metric-widget.component.html',
  imports: [NgClass],
  styleUrls: ['./metric-widget.component.scss', './chart.scss']
})
export class MetricWidgetComponent implements OnInit, OnDestroy {
  @ViewChild('OrMetricWidgetChartContent') chartContent: ElementRef;
  @Input() metric: Metric;
  @Output() setActive = new EventEmitter<DataType>();
  @Input() isActive: boolean;
  @Input({ required: true }) building: Building;
  @Input({ required: true }) floorId: number;
  canZoomIn = signal(false);
  canZoomOut = signal(false);
  canShiftTimeframe = signal(false);
  isBusy = signal(true);
  timeframeLabel = signal<string>(null);
  private destroy$ = new Subject<void>();
  private data: ChartData;
  private requestQueue$ = new BehaviorSubject<IQueryContext | null>(null);

  constructor(
    private queryExecutor: QueryExecutorService,
    private timeNavigator: TimeNavigatorService,
    private copyToClipboardService: CopyToClipboardService,
    private toastService: ToastService,
    private metricService: AnalyticsMetricsService
  ) {}

  ngOnInit(): void {
    this.metricService.updateContextBuilding(this.building.id, this.floorId);
    this.metricService.currentContext$.pipe(takeUntil(this.destroy$)).subscribe((metricContext) => {
      this.timeNavigator.reset();
      this.timeNavigator.setDates(metricContext.dates);
      this.timeNavigator.setResolution(metricContext.resolution);
      this.timeframeLabel.set(this.timeNavigator.getLabel().slice(0, 15));
      this.enqueueRequest(metricContext);
    });
    this.processQueue();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  copyToClipboard(): void {
    if (this.data != null) {
      this.copyToClipboardService.copyString(this.data.asCsvData(Pair.of('Time', this.metric.label)));
      this.toastService.success({
        message: 'Copied data to your clipboard! Paste into a spreadsheet to import the data.',
        dataCy: 'copied-to-clipboard'
      });
    }
  }

  private enqueueRequest(context: IQueryContext): void {
    this.requestQueue$.next(context);
  }

  private processQueue(): void {
    this.requestQueue$
      .pipe(
        filter((context) => context != null),
        concatMap((context) => this.executeQuery(context))
      )
      .subscribe();
  }

  // We are now queuing the requests in order to maintain the order in which
  // the context was updated, this way we will not overwrite the chart data
  // we want to see with the data from the previous context.
  private executeQuery(context: IQueryContext): Observable<void> {
    return new Observable<void>((observer) => {
      if (context.dataType !== this.metric.dataType) {
        this.update();
        observer.complete();
        return;
      }
      this.isBusy.set(true);
      const currentScale = this.timeNavigator.getScale();
      this.queryExecutor
        .doComplexQuery(this.metricService.getNavigationOutline())
        .pipe(takeUntil(this.destroy$))
        .subscribe((result: QueryResult<number>) => {
          if(this.timeNavigator.getScale() !== currentScale) {
            // If the scale has changed during the query, we discard the result
            observer.complete();
            return;
          }
          this.update();
          this.isBusy.set(false);
          this.data = this.convertToChart(result, context);
          this.render(this.data);
          observer.complete();
        });
    });
  }

  render(chart: ChartData): void {
    if (this.chartContent?.nativeElement) {
      const renderableChart = new RenderableChart(
        chart,
        this.chartContent.nativeElement,
        () => this.timeNavigator.canSelect(),
        (points) => this.select(points),
        (amount) => this.timeNavigator.canZoom(amount),
        (amount, point?: DataPoint) => this.zoom(amount, point)
      );
      renderableChart.render();
    }
  }

  setActiveAndRefresh(): void {
    this.setActive.emit(this.metric.dataType);
    this.metricService.updateContextDataType(this.metric.dataType);
    this.metricService.updateQueryParams();
  }

  zoomIn(): void {
    this.zoom(1);
  }

  zoomOut(): void {
    this.zoom(-1);
  }

  select(points: DataPoint[]): void {
    this.timeNavigator.select(points.map((point) => point.x));
    this.metricService.updateContextDateAndResolution(
      this.timeNavigator.getDates(),
      this.timeNavigator.getResolution()
    );
    this.metricService.updateQueryParams();
  }

  shiftTimeframeForward(): void {
    this.shiftTimeframe(1);
    this.metricService.updateQueryParams();
  }

  shiftTimeframeBack(): void {
    this.shiftTimeframe(-1);
    this.metricService.updateQueryParams();
  }

  private convertToChart(result: QueryResult<number>, context: IQueryContext): ChartData {
    const dataType = context.dataType;
    const chart = ChartData.fromQueryResult(result);
    chart.values.forEach((point) => {
      point.isActive = this.timeNavigator.isActive(point.x);
      point.xLabel = this.timeNavigator.getPointLabel(point.x);
      point.yLabel = dataType.format(point.y, result.suffix);
    });
    return chart;
  }

  private shiftTimeframe(amount: number): void {
    this.timeNavigator.shift(amount);
    this.metricService.updateContextDateAndResolution(
      this.timeNavigator.getDates(),
      this.timeNavigator.getResolution()
    );
  }

  private update(): void {
    this.timeframeLabel.set(this.timeNavigator.getLabel().slice(0, 15));
    this.canZoomIn.set(this.timeNavigator.canZoom(1));
    this.canZoomOut.set(this.timeNavigator.canZoom(-1));
    this.canShiftTimeframe.set(this.timeNavigator.canShift());
  }

  private zoom(amount: number, target?: DataPoint): void {
    if (target) {
      // zoom will be called with a target when user double-clicks
      // on a point in the chart. in that case, we select that point
      // and zoom in on it.
      this.timeNavigator.select([target.x]);
    }
    this.timeNavigator.zoom(amount);
    this.metricService.updateContextDateAndResolution(
      this.timeNavigator.getDates(),
      this.timeNavigator.getResolution()
    );
    this.metricService.updateQueryParams();
  }
}
